import axios from "axios";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";
import theme from "../../styles/theme";
import { PillarType } from "../../types/Roadmap";
import Button from "../../components/Button";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import Input, { InputTextArea } from "../../components/Input";
import { StyledText } from "../../components/Text";
import { Icon as MuiIcon } from "@mui/material";
import Icon from "../../components/Icon";
import Status from "../../types/enum/Status";
import { useNavigate } from "react-router-dom";
import { ColorResult, SketchPicker } from "react-color";
import IconPickerModal from "../../components/IconPickerModal";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { UserContext } from "../../context/UserContext";

export const CustomFlexFixed = styled(FlexBox)`
  width: 100%;
  position: fixed;
  bottom: 0;
  right: 0px;
  background: #ffffff;
  padding: 6px 0px;
  box-shadow: 0 75px 45px 45px black;
  // z-index: 9999;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px 0;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 200px;
  flex-grow: 0;
`;
const Label = styled.h5`
  text-transform: uppercase;
  color: ${theme.color.darkBlueGray900};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
const LabelData = styled(StyledText)`
  color: ${theme.color.lightBlueGray500};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  vertical-align: text-top;
`;
const SaveButton = styled(Button)`
  background: ${({ theme }) => theme.color.blueButton};
  box-shadow: 0 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;
export const CancelButton = styled(Button)`
  margin-right: 10px;
  vertical-align: bottom;
  background: ${({ theme }) => theme.color.deleteButton};
  box-shadow: 0 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;
type PillarOverviewType = {
  currentId?: any;
  isOpen?: boolean;
  header: string;
  mode: "modify" | "create";
  setOpen: (x: boolean) => void;
  setIsErrorOpen: (x: boolean) => void;
  isSnackbarOpen: boolean;
  setIsSnackbarOpen: (x: boolean) => void;
  setMessage: (x: string) => void;
  onSaveComplete: () => void;
  role: string;
};
const schema = yup
  .object({
    id: yup.string(),
    is_core: yup.boolean(),
    ordinal: yup.number(),
    title: yup.string().required(),
    // pillar_code: yup.string().required(),
    pillar_prefix: yup
      .string()
      .length(3, "Must include 3 letters")
      .matches(/^[A-Z]+$/, "Wrong pattern")
      .required("This field is required"),
    description: yup.string().required(),
    icon: yup.string().required(),
    color: yup.string(),
  })
  .required();
const EditPillar = ({
  isOpen,
  setOpen,
  onSaveComplete,
  isSnackbarOpen,
  setIsSnackbarOpen,
  setIsErrorOpen,
  setMessage,
  currentId,
  role,
}: PillarOverviewType) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      color: "#fff",
      icon: "",
      id: "",
      is_core: false,
      ordinal: 0,
    },
  });
  const [pillars, setPillars] = useState<
    Array<{
      label: string;
      value: string;
      selected: boolean;
      index: string;
      graphics: any;
    }>
  >([]);
  const [iconPickerOpen, setIconPickerOpen] = useState<boolean>(false);
  const onSubmitOverview = (data: any) => onSaveComplete();
  const [count, setCount] = useState({ name: 0, description: 0 });
  useEffect(() => {
    fetchPillars();
  }, [currentId.pillar_id]);

  const fetchPillars = () => {
    axios
      .get(`/pillar?pillar_id=${currentId.pillar_id}`)
      .then((res) => {
        mapPillars(res.data.pillars[0]);
      })
      .catch((error: any) => {
        setIsErrorOpen(true);
        setMessage(error?.response?.data?.error || "Failed to get pillar!");
      });
  };

  const editPillar = (data: any) => {
    axios
      .put("/pillar/update", { ...data, id: currentId.pillar_id })
      .then((res: any) => {
        setIsSnackbarOpen(true);
        setMessage("Updated Pillar Successfully!");
        onSaveComplete();
      })
      .catch((error: any) => {
        setIsErrorOpen(true);
        setMessage(error?.response?.data?.error || "Failed to edit pillar!");
      });
  };

  const mapPillars = (data: any) => {
    setValue("title", data.title || "");
    setValue("description", data?.description || "");
    // setValue("pillar_code", data?.code || "");
    setValue("pillar_prefix", data?.prefix || "");
    setValue("icon", data?.graphics?.icon || "");
    setValue("color", data?.graphics?.iconbg || "");
    setCount({
      ...count,
      description: data?.description?.length,
      name: data?.title?.length,
    });
  };
  return (
    <>
      <IconPickerModal
        isOpen={iconPickerOpen}
        setOpen={setIconPickerOpen}
        onSubmit={(icon) => {
          setValue("icon", icon);
          clearErrors("icon");
          setIconPickerOpen(false);
        }}
        data={{ icon: watch().icon }}
      />
      <form
        style={{
          borderBottom: "none",
          alignItems: "flex-start",
          // overflowY: "scroll",
          padding: 15,
        }}
        onSubmit={handleSubmit(editPillar)}
      >
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillar Name</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      isFullWidth
                      style={{ width: "100%" }}
                      maxLength={64}
                      value={field.value}
                      onChange={(e: any) => {
                        field.onChange(e);
                        setCount({ ...count, name: e.target.value.length });
                      }}
                    />
                  )}
                />
                {/* <Input isFullWidth {...register("title", { required: true })} /> */}
              </FlexGrow>
              <FlexGrow style={{ width: "100%", textAlign: "end" }}>
                <StyledText
                  style={{ fontSize: "14px" }}
                  color={theme.color.lightBlueGray500}
                >
                  {count.name}/64
                </StyledText>
              </FlexGrow>
            </FlexBox>
            {errors.title && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillar Description</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <InputTextArea
                      value={field.value}
                      isFullWidth
                      style={{ width: "100%", minHeight: "100px" }}
                      // value={field.value}
                      maxLength={256}
                      onChange={(e: any) => {
                        field.onChange(e);
                        setCount({
                          ...count,
                          description: e.target.value.length,
                        });
                      }}
                    />
                  )}
                />
              </FlexGrow>
              <FlexGrow style={{ width: "100%", textAlign: "end" }}>
                <StyledText
                  style={{ fontSize: "14px" }}
                  color={theme.color.lightBlueGray500}
                >
                  {count.description}/256
                </StyledText>
              </FlexGrow>
            </FlexBox>
            {errors.description && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillar Prefix</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox space={0} alignItems="start" style={{ width: "100%" }}>
              <FlexGrow style={{ width: "100%" }}>
                <Input
                  style={{ width: "100%" }}
                  isFullWidth
                  {...register("pillar_prefix", {
                    required: true,
                  })}
                />
              </FlexGrow>
            </FlexBox>
            {errors.pillar_prefix && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errors.pillar_prefix.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Icon</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <>
              {watch().icon && watch().icon !== "" && (
                <MuiIcon
                  sx={{
                    color: "black",
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                >
                  {watch().icon}
                </MuiIcon>
              )}
              <Button type="button" onClick={() => setIconPickerOpen(true)}>
                Select Icon
              </Button>
              {errors.icon && (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.red800,
                  }}
                >
                  Select icon for new pillar
                </p>
              )}
            </>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Color</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Controller
              control={control}
              name="color"
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <SketchPicker
                  color={value}
                  onChangeComplete={(
                    color: ColorResult,
                    event: ChangeEvent<HTMLInputElement>
                  ) => {
                    onChange(color.hex);
                  }}
                />
              )}
              rules={{ required: "This is required!" }}
            />
            {errors.color && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <FlexBox
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            padding: "10px 0px",
          }}
        >
          <FlexGrow></FlexGrow>
          <SaveButton
            disabled={role === "instructor"}
            className="reduce_opct_onhover"
            style={{
              padding: "8px 40px",
              color: "white",
            }}
          >
            <Icon style={{ paddingRight: "8px" }} name={"save"} />
            Save
          </SaveButton>
        </FlexBox>
      </form>
    </>
  );
};
export default EditPillar;
